import {mapSetState, SetState} from "../../util/FormHandlers";
import {HoodItem, SeatingPlanModel} from "./SeatingPlanModel";
import {Autocomplete, Button, Grid, Stack, TextField, Typography} from "@mui/material";
import React, {useMemo} from "react";
import EmployeeAvatar from "../../components/employee-avatar/EmployeeAvatar";
import NeighbourhoodColorIcon from "../../components/neighbourhood-icon/NeighbourhoodColorIcon";
import {Desk, Employee, EmployeeRef} from "../../api/Model";
import {EmployeeSelect} from "../../components/employee-select/EmployeeSelect";
import {emptyEmployee} from "../../api/Empty";

export type ChangeDeskAssignmentForm = {
    desk: Desk,
    employee: Employee | undefined,
    neighbourhood: HoodItem | null,
    previousEmployee: EmployeeRef | null,
    previousNeighbourhood: HoodItem | null,
};

export function ChangeDeskAssignmentDialog({form, setForm, model}: {
    form: ChangeDeskAssignmentForm,
    setForm: SetState<ChangeDeskAssignmentForm>,
    model: SeatingPlanModel,
}) {
    const handlers = useMemo(()=> ({
        setEmployee: mapSetState(setForm, 'employee'),
    }), [setForm]);

    return (
        <Grid container spacing={2} alignItems="center">
            <Grid size={5}>
                <Typography variant="h6">Current</Typography>
            </Grid>
            <Grid size={7}>
                <Typography variant="h6">New Value</Typography>
            </Grid>

            <Grid size={5}>
                <Stack direction="row" gap={1} alignItems="center">
                    {form.previousEmployee ? (
                        <>
                            <EmployeeAvatar employee={form.previousEmployee} sx={{width: 36, height: 36}}/>
                            <Typography variant="body1">{form.previousEmployee.fullName}</Typography>
                        </>
                    ) : (
                        <Typography variant="body1" color="textSecondary">Shared</Typography>
                    )}
                </Stack>
            </Grid>
            <Grid size={7}>
                <EmployeeSelect
                    label="Assign desk to employee"
                    placeholder="Shared desk"
                    employee={form.employee}
                    setEmployee={handlers.setEmployee}
                    clearable
                />
            </Grid>

            <Grid size={5} container direction="row" gap={1} alignItems="center">
                {form.previousNeighbourhood ? (
                    <>
                        <NeighbourhoodColorIcon
                            neighbourhood={form.previousNeighbourhood}
                            size={36}
                        />
                        <Typography variant="body1">{form.previousNeighbourhood.name}</Typography>
                    </>
                ) : (
                    <Typography variant="body1" color="textSecondary">None</Typography>
                )}
            </Grid>
            <Grid size={7}>
                <Autocomplete
                    options={model.assignableHoodItems}
                    value={form.neighbourhood}
                    onChange={(_event, neighbourhood) => setForm(prev => ({...prev, neighbourhood}))}
                    getOptionLabel={neighbourhood => neighbourhood.name}
                    isOptionEqualToValue={SeatingPlanModel.isEqualHood}
                    renderInput={params => <TextField {...params} label="Neighbourhood" placeholder="None"/>}
                />
            </Grid>
        </Grid>
    );
}

export function RevertDeskAssignmentButton({form, setForm}: {
    form: ChangeDeskAssignmentForm,
    setForm: SetState<ChangeDeskAssignmentForm>,
}) {
    const isPreviousAssignment = form.employee?.id === form.previousEmployee?.id
        && form.neighbourhood?.key === form.previousNeighbourhood?.key;

    return (
        <Button
            variant="outlined"
            color="warning"
            disabled={isPreviousAssignment}
            onClick={() => setForm(prev => ({
                ...prev,
                employee: prev.previousEmployee ? {...emptyEmployee, ...prev.previousEmployee} : undefined,
                neighbourhood: prev.previousNeighbourhood,
            }))}
        >
            Revert to previous values
        </Button>
    );
}
